/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { HttpParams } from '@angular/common/http';
import { ApiService } from '@api/services/api/api.service';
import {
  PositionDTO,
  IdentificationVerifyStatus,
  NoteDTO,
  DigitalSigningDTO
} from '@api/models/';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Position } from '@models/position';

@Injectable({
  providedIn: 'root'
})
export class PositionService {
  private position$: BehaviorSubject<Position> = new BehaviorSubject<Position>(
    null
  );

  constructor(private apiService: ApiService) {}

  /**
   * carica i dati della position richiesta
   */
  loadPosition(positionId: string): Observable<boolean> {
    if (!positionId) {
      return throwError('wrn.position_loading_error');
    }

    return this.apiService.getPosition(positionId).pipe(
      map(res => this.setPosition(res)),
      map(() => true)
    );
  }

  /**
   * ricarica la position
   */
  reloadPosition(): Observable<boolean> {
    return this.loadPosition(this.getPositionId());
  }

  /**
   * restituisce osservabile per dati position
   */
  getPositionWhenChange(): Observable<Position> {
    return this.position$
      .asObservable()
      .pipe(filter(x => x instanceof Position));
  }

  /**
   * restituisce i dati della position
   */
  getPosition(): Position {
    return this.position$.getValue();
  }

  /**
   * restituisce il position id
   */
  getPositionId(): string {
    const position = this.getPosition();
    return position && position.data.positionId;
  }

  /**
   * restituisce l'url per attivare il flusso di riconoscimento
   */
  getRecognitionUrl(method: string): Observable<string> {
    const position = this.getPosition();

    return this.apiService
      .postIdentificationStart(method, {
        businessId: position.data.positionId,
        ndg: position.data.ndg
      })
      .pipe(map(res => res.consumerUrl));
  }

  /**
   * controlla che la procedura di riconoscimento sia stata completata
   */
  verifyRecognition(method: string): Observable<IdentificationVerifyStatus> {
    const position = this.getPosition();

    const params = new HttpParams()
      .set('businessId', position.data.positionId)
      .set('ndg', position.data.ndg);

    return this.apiService
      .getIdentificationVerify(method, { params })
      .pipe(map(res => (res && res.status) || null));
  }

  /**
   * avvia nuova sessione di firma digitale
   */
  startDigitalSigningSession(): Observable<DigitalSigningDTO> {
    const position = this.getPosition();

    return this.apiService.getDigitalSignatureRequestDsp(
      position.data.positionId
    );
  }

  /**
   * avvia firma digitale già in corso (avviata precedentemente)
   */
  resumeDigitalSigningSession(): Observable<DigitalSigningDTO> {
    const position = this.getPosition();

    return this.apiService.getDigitalSignatureSign(position.data.positionId);
  }

  /**
   * restituisce lo status della sessione di firma digitale
   */
  getDigitalSigningSessionStatus(): Observable<DigitalSigningDTO> {
    const position = this.getPosition();

    return this.apiService.getDigitalSignatureRequestStatusDsp(
      position.data.positionId
    );
  }

  /**
   * ritira la pratica
   */
  withdrawPosition(reasonCode: string): Observable<any> {
    const positionId = this.getPositionId();

    return this.apiService.postPositionWithdraw(positionId, { reasonCode });
  }

  /**
   * ritorna la lista di note
   */
  getNotes(type: string): Observable<NoteDTO[]> {
    const positionId = this.getPositionId();

    return this.apiService.getNotes(positionId, type);
  }

  /**
   * invia codice otp sul canale richiesto verso il contatto richiesto
   */
  sendOpt(channel: 'SMS' | 'MAIL', contact: string): Observable<any> {
    const positionId = this.getPositionId();

    return this.apiService.postContactSendOtp(positionId, channel, { contact });
  }

  /**
   * verifica il codice otp
   */
  verifyOpt(
    channel: 'SMS' | 'MAIL',
    contact: string,
    otpCode: string
  ): Observable<boolean> {
    const positionId = this.getPositionId();

    return this.apiService
      .putContactVerifyOtp(positionId, channel, {
        contact,
        otp: otpCode
      })
      .pipe(map(() => true));
  }

  /**
   * controlla se sulla position è presente il task
   */
  doesPositionHasTask(taskCode: string): Observable<boolean> {
    const params = new HttpParams().set('locked', 'true');

    return this.apiService
      .getPersonalTasks({ params })
      .pipe(
        map(
          res =>
            !!res.tasks.find(
              task =>
                task.businessId === this.getPositionId() &&
                task.code === taskCode
            )
        )
      );
  }

  /**
   * imposta la position
   */
  private setPosition(dto: PositionDTO): void {
    this.position$.next(new Position(dto));
  }
}
